import { useState, useEffect } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFormDetail, setFormTheme } from "../lib/api-client";

interface ThemePreview {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  fontFamily: string;
  fontSize: string;
  borderRadius: string;
}

function FormThemeEditorPage(): JSX.Element {
  const { formId } = useParams<{ teamId: string; formId: string }>();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<ThemePreview>({
    primaryColor: "#3b82f6",
    secondaryColor: "#10b981",
    backgroundColor: "#ffffff",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontSize: "16px",
    borderRadius: "8px",
  });
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const formQuery = useQuery({
    queryKey: ["form-detail", formId],
    queryFn: () => fetchFormDetail(formId!),
    enabled: Boolean(formId),
  });

  const setThemeMutation = useMutation({
    mutationFn: async (themeId: string | null) => {
      if (!formId) throw new Error("formId missing");
      return setFormTheme(formId, { themeId });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["form-detail", formId] });
      setToast({ type: "success", message: "Theme applied successfully." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to apply theme.",
      });
    },
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const teamTheme = formQuery.data?.teamTheme;
  const formTheme = formQuery.data?.theme;

  const containerStyle: CSSProperties = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "2rem",
  };

  const cardStyle: CSSProperties = {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-base)",
    padding: "1.5rem",
    marginBottom: "2rem",
  };

  const formGroupStyle: CSSProperties = {
    marginBottom: "1rem",
  };

  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
    marginBottom: "0.5rem",
    color: "var(--color-text)",
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    padding: "0.625rem 0.875rem",
    border: "1px solid var(--color-border)",
    borderRadius: "calc(var(--radius-base) - 2px)",
    fontSize: "0.875rem",
    fontFamily: "inherit",
  };

  const previewStyle: CSSProperties = {
    background: preview.backgroundColor,
    borderRadius: preview.borderRadius,
    padding: "2rem",
    marginTop: "1rem",
    fontFamily: preview.fontFamily,
    fontSize: preview.fontSize,
  };

  const previewButtonStyle: CSSProperties = {
    background: preview.primaryColor,
    color: "white",
    border: "none",
    padding: "0.75rem 1.5rem",
    borderRadius: preview.borderRadius,
    cursor: "pointer",
    fontWeight: 500,
    marginRight: "0.5rem",
    marginBottom: "0.5rem",
  };

  const previewSecondaryButtonStyle: CSSProperties = {
    background: preview.secondaryColor,
    color: "white",
    border: "none",
    padding: "0.75rem 1.5rem",
    borderRadius: preview.borderRadius,
    cursor: "pointer",
    fontWeight: 500,
    marginRight: "0.5rem",
    marginBottom: "0.5rem",
  };

  return (
    <div style={containerStyle}>
      <h1 style={{ fontSize: "1.75rem", fontWeight: 600, marginBottom: "2rem" }}>
        Form Theme Editor
      </h1>

      {/* Current Theme Info */}
      {(formTheme || teamTheme) && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem" }}>
            Current Theme
          </h2>
          <p style={{ color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
            {formTheme ? `Form Theme: ${formTheme.name}` : `Team Theme: ${teamTheme?.name}`}
          </p>
          {formTheme && (
            <button
              type="button"
              className="button ghost"
              onClick={() => setThemeMutation.mutate(null)}
              disabled={setThemeMutation.isPending}
              style={{ fontSize: "0.875rem", color: "var(--color-critical)" }}
            >
              Remove Form Theme
            </button>
          )}
        </div>
      )}

      {/* Color Customization */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Colors
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Primary Color</label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                type="color"
                value={preview.primaryColor}
                onChange={(e) => setPreview({ ...preview, primaryColor: e.target.value })}
                style={{ width: "50px", height: "40px", border: "1px solid var(--color-border)", cursor: "pointer" }}
              />
              <input
                type="text"
                value={preview.primaryColor}
                onChange={(e) => setPreview({ ...preview, primaryColor: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Secondary Color</label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                type="color"
                value={preview.secondaryColor}
                onChange={(e) => setPreview({ ...preview, secondaryColor: e.target.value })}
                style={{ width: "50px", height: "40px", border: "1px solid var(--color-border)", cursor: "pointer" }}
              />
              <input
                type="text"
                value={preview.secondaryColor}
                onChange={(e) => setPreview({ ...preview, secondaryColor: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Background Color</label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                type="color"
                value={preview.backgroundColor}
                onChange={(e) => setPreview({ ...preview, backgroundColor: e.target.value })}
                style={{ width: "50px", height: "40px", border: "1px solid var(--color-border)", cursor: "pointer" }}
              />
              <input
                type="text"
                value={preview.backgroundColor}
                onChange={(e) => setPreview({ ...preview, backgroundColor: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Typography */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Typography
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Font Family</label>
            <select
              value={preview.fontFamily}
              onChange={(e) => setPreview({ ...preview, fontFamily: e.target.value })}
              style={inputStyle}
            >
              <option value="system-ui, -apple-system, sans-serif">System Default</option>
              <option value="Georgia, serif">Georgia (Serif)</option>
              <option value="'Courier New', monospace">Courier (Monospace)</option>
              <option value="'Trebuchet MS', sans-serif">Trebuchet (Sans-serif)</option>
              <option value="Verdana, sans-serif">Verdana (Sans-serif)</option>
            </select>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Font Size</label>
            <input
              type="text"
              value={preview.fontSize}
              onChange={(e) => setPreview({ ...preview, fontSize: e.target.value })}
              placeholder="e.g., 16px"
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Spacing & Borders */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Spacing & Borders
        </h2>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Border Radius</label>
          <input
            type="text"
            value={preview.borderRadius}
            onChange={(e) => setPreview({ ...preview, borderRadius: e.target.value })}
            placeholder="e.g., 8px"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Preview */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Preview
        </h2>

        <div style={previewStyle}>
          <h3 style={{ margin: "0 0 1rem 0", color: preview.primaryColor }}>
            Form Title
          </h3>
          <p style={{ margin: "0 0 1rem 0", color: "#666" }}>
            This is how your form will look with the selected theme.
          </p>
          <div style={{ marginTop: "1rem" }}>
            <button style={previewButtonStyle}>Primary Button</button>
            <button style={previewSecondaryButtonStyle}>Secondary Button</button>
          </div>
          <div style={{ marginTop: "1rem", padding: "1rem", background: "rgba(0,0,0,0.05)", borderRadius: preview.borderRadius }}>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500 }}>
              Sample Input Field
            </label>
            <input
              type="text"
              placeholder="Enter text..."
              style={{
                width: "100%",
                padding: "0.625rem",
                border: `1px solid ${preview.primaryColor}`,
                borderRadius: preview.borderRadius,
                fontFamily: preview.fontFamily,
                fontSize: preview.fontSize,
              }}
            />
          </div>
        </div>
      </div>

      {/* Theme Templates */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          Theme Templates
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
          {[
            { name: "Blue", primary: "#3b82f6", secondary: "#10b981" },
            { name: "Purple", primary: "#8b5cf6", secondary: "#ec4899" },
            { name: "Green", primary: "#10b981", secondary: "#06b6d4" },
            { name: "Orange", primary: "#f97316", secondary: "#eab308" },
          ].map((template) => (
            <button
              key={template.name}
              type="button"
              onClick={() =>
                setPreview({
                  ...preview,
                  primaryColor: template.primary,
                  secondaryColor: template.secondary,
                })
              }
              style={{
                padding: "1rem",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-base)",
                background: "var(--color-bg-solid)",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = template.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-border)";
              }}
            >
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    background: template.primary,
                    borderRadius: "4px",
                  }}
                />
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    background: template.secondary,
                    borderRadius: "4px",
                  }}
                />
              </div>
              <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>{template.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            background: toast.type === "success" ? "var(--color-success)" : "var(--color-critical)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "var(--radius-base)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 1001,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default FormThemeEditorPage;
